// Reviews · client testimonials block.
// Fetches approved reviews from /api/reviews and renders structured
// cards · profile photo + name + profession + star rating + review
// body. Falls back to a curated default set when the collection is
// empty so the section never appears blank.
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Star, Quote } from "lucide-react";
import api from "@/lib/api";
import ReviewSubmitForm from "@/components/ReviewSubmitForm";

const FALLBACK_REVIEWS = [
  {
    id: "f1",
    name: "Marcus Chen",
    profession: "Day Trader · Singapore",
    rating: 5,
    body:
      "Tight spreads and rock-solid MT5 execution. Funding lands the same day and withdrawals are equally quick. Best broker switch I have made in years.",
    photo_url: null,
  },
  {
    id: "f2",
    name: "Aisha Rahman",
    profession: "Scalper · UAE",
    rating: 5,
    body:
      "The Elite raw account fits my scalping style perfectly · spreads from zero, low commission and zero slippage on majors. Onboarding KYC was painless.",
    photo_url: null,
  },
  {
    id: "f3",
    name: "Lukas Weber",
    profession: "Swing Trader · Germany",
    rating: 5,
    body:
      "Transparent pricing, fast funding and an account manager who actually answers. The MT5 web terminal is finally good enough that I do not need the desktop client.",
    photo_url: null,
  },
];

function Avatar({ photo_url, name, size = 44 }) {
  const initial = (name || "·").trim().charAt(0).toUpperCase();
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const src = photo_url
    ? (photo_url.startsWith("http") ? photo_url : `${backend}${photo_url}`)
    : null;
  return src ? (
    <img
      src={src}
      alt={name || "reviewer"}
      width={size}
      height={size}
      loading="lazy"
      className="rounded-full object-cover shrink-0"
      style={{ width: size, height: size, border: "1px solid var(--line)" }}
    />
  ) : (
    <div
      className="rounded-full grid place-items-center shrink-0 font-display font-medium"
      style={{
        width: size,
        height: size,
        background:
          "linear-gradient(135deg, color-mix(in srgb, var(--brand) 80%, white), var(--brand-deep))",
        color: "var(--brand-ink)",
        fontSize: size > 40 ? 16 : 13,
      }}
    >
      {initial}
    </div>
  );
}

function ReviewCard({ r, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.06, duration: 0.42 }}
      className="card-beam p-6 flex flex-col"
      data-testid={`review-card-${i}`}
    >
      <Quote
        size={18}
        strokeWidth={1.6}
        style={{ color: "var(--brand)" }}
        className="mb-3"
      />
      <div className="flex gap-0.5 mb-3" aria-label={`${r.rating} of 5 stars`}>
        {Array.from({ length: 5 }).map((_, idx) => (
          <Star
            key={idx}
            size={13}
            strokeWidth={1.6}
            className={idx < (r.rating || 5) ? "fill-current" : ""}
            style={{
              color: idx < (r.rating || 5) ? "var(--brand)" : "var(--text-3)",
            }}
          />
        ))}
      </div>
      <p className="text-text-2 text-[13.5px] leading-relaxed mb-5 flex-1 font-light">
        “{r.body || r.content}”
      </p>
      <div
        className="flex items-center gap-3 pt-4 border-t"
        style={{ borderColor: "var(--line)" }}
      >
        <Avatar photo_url={r.photo_url} name={r.name} />
        <div className="min-w-0">
          <div className="font-display text-text-1 font-medium text-[13.5px] truncate">
            {r.name}
          </div>
          <div className="font-mono text-[12px] text-text-3 uppercase tracking-[0.16em] truncate">
            {r.profession || r.country || "Trader"}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Reviews() {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);

  const load = () => {
    api
      .get("/reviews?limit=24")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setReviews(data);
        else setReviews(FALLBACK_REVIEWS);
      })
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-14 lg:py-24"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <div className="pill-mono inline-flex mb-5">{t("reviews.pill", "Verified client reviews")}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1 mb-3">
            {t("reviews.h1", "What our traders say")}
            <br className="hidden sm:block" />{" "}
            <span className="text-gradient-brand font-semibold">{t("reviews.hAccent", "in their own words")}</span>
          </h2>
          <p className="text-text-3 max-w-2xl mx-auto text-[14.5px] lg:text-[15px] font-light leading-relaxed">
            {t("reviews.sub", "Honest feedback from active OFinancial Markets clients. Share your own story below — every review is read and verified before it goes live.")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.slice(0, 9).map((r, i) => (
            <ReviewCard key={r.id || i} r={r} i={i} />
          ))}
        </div>

        <ReviewSubmitForm onSubmitted={load} />
      </div>
    </section>
  );
}
